import { Box, Typography } from "@mui/material";
import { boardTokens, OFF_WHITE, DARK_SURFACES } from "@/shared/theme";
import { evaluationBarWhiteEdge, evaluationPresentation, startPositionAware, type Evaluation } from "./evaluation";

export interface EvaluationBarProps {
  layout: "vertical-left" | "horizontal-bottom";
  evaluation: Evaluation | null;
  orientation: "white" | "black";
  label?: string;
  pending?: boolean;
  checkmateWinner?: "white" | "black";
  /** Displayed position; the starting position always reads 0.0. */
  fen?: string;
}

export const EvaluationBar = ({
  layout,
  evaluation,
  orientation,
  label,
  pending = false,
  checkmateWinner,
  fen,
}: EvaluationBarProps) => {
  const presentation = checkmateWinner
    ? { percent: checkmateWinner === "white" ? 100 : 0, label: "Checkmate" }
    : evaluationPresentation(startPositionAware(fen, evaluation));
  const percent = presentation.percent;
  const known = presentation.label !== "Unknown";
  const value = pending && !known ? "CALCULATING…" : known ? presentation.label : "";
  const vertical = layout === "vertical-left";
  const whiteEdge = evaluationBarWhiteEdge(layout, orientation);
  const labelOnWhite = known && percent >= 50;
  const labelEdge = labelOnWhite ? whiteEdge : whiteEdge === "top" ? "bottom" : "top";

  return (
    <Box
      aria-label={
        value
          ? label
            ? `Evaluation ${value} ${label}`
            : checkmateWinner
              ? `Checkmate · ${checkmateWinner === "white" ? "White" : "Black"} wins`
              : `Evaluation: ${value}`
          : label
            ? `Evaluation ${label}`
            : "Evaluation unavailable"
      }
      {...(known
        ? { "aria-valuemin": 0, "aria-valuemax": 100, "aria-valuenow": percent, "aria-valuetext": value }
        : {})}
      data-testid="evaluation-bar"
      role={known ? "meter" : "img"}
      sx={{
        position: "relative",
        overflow: "hidden",
        flexShrink: 0,
        ...(vertical
          ? {
              width: { xs: boardTokens.evaluation.mobileWidth, sm: boardTokens.evaluation.width },
              alignSelf: "stretch",
              bgcolor: boardTokens.evaluation.background,
            }
          : { height: boardTokens.evaluation.height, bgcolor: "background.paper" }),
      }}
    >
      {known && (
        <Box
          data-evaluation-white-fill
          aria-hidden
          sx={{
            position: "absolute",
            bgcolor: OFF_WHITE.main,
            transition: `${vertical ? "height" : "width"} ${boardTokens.evaluation.transitionMs}ms ease`,
            "@media (prefers-reduced-motion: reduce)": { transition: "none" },
            ...(vertical
              ? { insetInline: 0, height: `${percent}%`, [whiteEdge]: 0 }
              : { insetBlock: 0, width: `${percent}%`, [whiteEdge]: 0 }),
          }}
        />
      )}
      {value && (
        <Typography
          data-evaluation-label-surface={labelOnWhite ? "white" : "black"}
          data-evaluation-label-color={labelOnWhite ? "ink" : "paper"}
          sx={{
            position: "absolute",
            zIndex: 1,
            color: labelOnWhite ? DARK_SURFACES.canvas : OFF_WHITE.main,
            ...(vertical
              ? {
                  // Phones show the thin bar only; the value stays in the accessible name.
                  display: { xs: "none", sm: "block" },
                  insetInline: 0,
                  [labelEdge]: 0,
                  py: 0.65,
                  textAlign: "center",
                  fontSize: boardTokens.evaluation.verticalFontSize,
                }
              : {
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  fontSize: boardTokens.evaluation.horizontalFontSize,
                }),
            fontWeight: boardTokens.evaluation.fontWeight,
            lineHeight: 1,
            whiteSpace: "nowrap",
          }}
        >
          {value}
        </Typography>
      )}
    </Box>
  );
};
